/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import axios from "axios";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_INACTIVE_BOXES } from "../graphql/getInactiveBoxes.query";
import config from "../config/userLists.config.json";
import settingConfig from "../config/settings.config.json";
import { VApplication } from "../../../application";
import moment from "moment-timezone";
import { pretty } from "@vlinder-web/utils-react";
import { GET_PRESENTATIONS } from "../graphql/getPresentations.query";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IMasqueraderListsMethods extends IPluginMethodMap {
  getMasqueraderListsProps(args: any): Promise<any>;
}

export class MasqueraderListsService implements IAgentPlugin {
  readonly methods: IMasqueraderListsMethods;

  constructor() {
    this.methods = {
      getMasqueraderListsProps: this.getMasqueraderListsProps.bind(this),
    };
  }

  async getMasqueraderListsProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let filter = {};
    let comm_pre_id: any = "",
      presentationOptions: any = [],
      defaultPresentation: any = "";
    const searchType = this.determineSearchType(args?.criteria);
    if (searchType && searchType !== null) {
      if (searchType === "multi") {
        filter = this.getMultiFilter(args?.criteria);
      } else {
        filter = this.convertCriteria(args?.criteria);
      }
    } else {
      filter = {
        ...filter,
        inactive___type: {
          eq: "Deleted",
        },
      };
    }
    const options = {
      sort: {
        field: "inactive.date",
        order: "desc",
      },
      limit: args?.size ?? 10,
      skip: args?.offset ?? 0,
    };
    const _filter = {
      // status: {
      //   eq: "active",
      // },
      // year: {
      //   eq: "2024",
      // },
    };
    try {
      if (client) {
        const presentation = await client.query({
          query: GET_PRESENTATIONS,
          variables: {
            filter: _filter,
            options: {
              limit: 10,
              skip: 0,
              sort: {
                field: "status",
                order: "asc",
              },
            },
          },
          fetchPolicy: "network-only",
        });

        if (
          presentation &&
          presentation?.data?.getPresentations?.content[0]?.id
        ) {
          defaultPresentation =
            presentation?.data?.getPresentations?.content[0]?.id;
          presentationOptions =
            presentation?.data?.getPresentations?.content?.map((_item: any) => {
              return { value: _item?.id, label: _item?.name };
            });
          if (searchType !== "multi") {
            filter = {
              ...filter,
              presentation: {
                eq: args?.criteria?.presentation ?? defaultPresentation,
              },
            };
          }
        }
        const res = await client.query({
          query: GET_INACTIVE_BOXES,
          variables: {
            options: options,
            filter: filter,
          },
          fetchPolicy: "network-only",
        });

        if (res?.data) {
          let _rowData = this.formatUserTable(
            res?.data?.getInactiveBoxes?.content
          );
          let _totalPages = res?.data?.getInactiveBoxes?.count
            ? Math.ceil(parseInt(res?.data?.getInactiveBoxes?.count) / 10)
            : 1;

          let _currentPage =
            res?.data?.getInactiveBoxes?.skip /
              res?.data?.getInactiveBoxes?.limit +
            1;
          return {
            ...config,
            ultUsers: _rowData,
            isEmpty: res?.data?.getInactiveBoxes?.content?.length <= 0,
            activePage: _currentPage,
            currentPage: _currentPage,
            pageSize: res?.data?.getInactiveBoxes?.limit,
            totalCount: res?.data?.getInactiveBoxes?.count,
            searchPresentationFilter: presentationOptions,
            tableHeaders: this.updatePresentationOptions(
              settingConfig.tableHeaders,
              presentationOptions
            ),
            totalPages: _totalPages,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private formatUserTable(data: any) {
    let _rowData: any = [];
    data.forEach((item: any) => {
      _rowData.push({
        id: item?.id,
        name: item?.user?.fullName ?? "Null Null",
        email: item?.user?.email ?? "null",
        gender: item?.user?.gender ?? "-",
        invitedBy: item?.user?.creatorUser?.fullName ?? "-",
        inviteType: item?.referal?.inviteType ?? "-",
        deletedBy: item?.inactive?.user?.fullName ?? "-",
        reason: item?.inactive?.reason ?? "-",
        costumeId: item?.costumeID ?? "-",
        amountPaid: item?.amountPaid ?? "-",
        totalAmount: item?.totalAmount ?? "-",
        presentation: item?.presentation?.name ?? "-",
        deletedDate: this.formatCreatedDate(item?.inactive?.date) ?? "-",
        section: item?.section?.name ?? "-",
        costumeTypes: item?.referal?.sectionOptType ?? "-",
        allDetails: item,
      });
    });
    return _rowData;
  }

  private determineSearchType(criteria: any) {
    if (Object.keys(criteria).length === 0) {
      return null;
    } else if (Object.keys(criteria)[0] === "0") {
      return "multi";
    } else {
      return "normal";
    }
  }

  private updatePresentationOptions(
    tableHeaders: any[],
    presentationOptions: any[]
  ) {
    const presentationHeader = tableHeaders.find(
      (header) => header.key === "Presentation"
    );
    if (presentationHeader) {
      const filterType = presentationHeader.filterTypes.find(
        (filter) => filter.id === "presentation" && filter.type === "select"
      );

      if (filterType) {
        filterType.options = presentationOptions;
      }
    }
    return tableHeaders;
  }

  private convertCriteria(criteria: any) {
    const result: any = {};
    for (const key in criteria) {
      result[key] = { contains: criteria[key] };
    }
    return result;
  }

  private formatCreatedDate(date: Date) {
    if (date) {
      return moment(date).format("LL");
    }
  }

  private getMultiFilter(criteria: any) {
    const transformedCriteria: any = {};
    for (const key in criteria) {
      if (criteria.hasOwnProperty(key)) {
        const { key: filterKey, filters } = criteria[key];
        const { inputData } = filters;
        const filterType = Object.keys(filters).find(
          (key) => key !== "inputData"
        );

        transformedCriteria[inputData.id] = {
          [filterType.toLowerCase().replace("equal", "eq")]:
            filters[filterType],
        };
      }
    }
    return transformedCriteria;
  }
}
